// Sivir Ricochet (W) VFX — a spinning golden crossblade is thrown from Sivir to the
// primary target, lingers briefly, then bounces to each nearby enemy in turn.
// Does NOT return to caster. Each hit: impact flash → linger glow → bounce to next.
//
// positions: [casterPos, primaryTarget, bounce1, bounce2, ...]
// onHit(i) fires when the blade arrives at positions[i+1] (target index i).

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const THROW_MS  = 165; // caster → primary target
const BOUNCE_MS = 120; // target → next target
const LINGER_MS =  80; // pause at each target before bouncing
const FADE_MS   = 110; // fade after last target
const BLADE_R   =  13;
const SPIN_RATE =  16; // rad/s

// --- drawing helpers ---

function drawBlade(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number,
  rot: number,
  alpha: number,
  scale = 1,
): void {
  const r = BLADE_R * scale;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(cx, cy);
  ctx.rotate(rot);

  for (let i = 0; i < 4; i++) {
    ctx.save();
    ctx.rotate((i / 4) * Math.PI * 2);

    const g = ctx.createLinearGradient(0, 0, r, 0);
    g.addColorStop(0,   "#ffe066");
    g.addColorStop(0.5, "#f09818");
    g.addColorStop(1,   "rgba(255,120,0,0)");
    ctx.fillStyle = g;
    ctx.shadowColor = "#ffaa00";
    ctx.shadowBlur  = 12;

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.bezierCurveTo(r * 0.25, -r * 0.22, r * 0.75, -r * 0.14, r,  0);
    ctx.bezierCurveTo(r * 0.75,  r * 0.14, r * 0.25,  r * 0.22, 0,  0);
    ctx.fill();

    // Edge highlight
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = "#fff0a0";
    ctx.lineWidth   = 1.1;
    ctx.shadowBlur  = 5;
    ctx.beginPath();
    ctx.moveTo(r * 0.1, -r * 0.06);
    ctx.bezierCurveTo(r * 0.4, -r * 0.17, r * 0.75, -r * 0.11, r * 0.95, 0);
    ctx.stroke();

    ctx.restore();
  }

  // Hub
  const hg = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.3);
  hg.addColorStop(0,   "#ffffff");
  hg.addColorStop(0.5, "#ffe066");
  hg.addColorStop(1,   "#f0a020");
  ctx.fillStyle   = hg;
  ctx.shadowColor = "#ffdd44";
  ctx.shadowBlur  = 18;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

// Phase types within the timeline
type Phase =
  | { kind: "travel"; from: Point; to: Point; hitIndex: number }
  | { kind: "linger"; at: Point; hitFired: boolean }
  | { kind: "fade";   at: Point };

export class SivirRicochetVFX implements Animation {
  private elapsed  = 0;
  private readonly phases: { phase: Phase; start: number; duration: number }[];
  private readonly total: number;
  private readonly firedHits: boolean[];
  // Tracks impact ring state per target for overlapping draw
  private readonly hitTimes: number[];
  onEnd?: () => void;

  constructor(
    private readonly positions: Point[], // [casterPos, t0, t1, ...]
    private readonly onHit: (index: number) => void,
  ) {
    const numTargets = positions.length - 1; // positions[0] is caster
    this.firedHits = new Array(numTargets).fill(false);
    this.hitTimes  = new Array(numTargets).fill(-1);

    const phases: { phase: Phase; start: number; duration: number }[] = [];
    let cursor = 0;

    for (let i = 0; i < numTargets; i++) {
      const from = i === 0 ? positions[0] : positions[i];
      const to   = positions[i + 1];
      const travelMs = i === 0 ? THROW_MS : BOUNCE_MS;

      phases.push({
        phase: { kind: "travel", from, to, hitIndex: i },
        start: cursor,
        duration: travelMs,
      });
      cursor += travelMs;

      const isLast = i === numTargets - 1;
      phases.push({
        phase: { kind: isLast ? "fade" : "linger", at: to, hitFired: false },
        start: cursor,
        duration: isLast ? FADE_MS : LINGER_MS,
      });
      cursor += isLast ? FADE_MS : LINGER_MS;
    }

    this.phases = phases;
    this.total  = cursor;
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.total);

    // Fire hit callbacks when the blade arrives (end of each travel phase)
    for (const { phase, start, duration } of this.phases) {
      if (phase.kind !== "travel") continue;
      const arrivalTime = start + duration;
      const i = phase.hitIndex;
      if (!this.firedHits[i] && this.elapsed >= arrivalTime) {
        this.firedHits[i] = true;
        this.hitTimes[i]  = arrivalTime;
        this.onHit(i);
      }
    }

    if (this.elapsed >= this.total) this.onEnd?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const rot = (this.elapsed / 1000) * SPIN_RATE;

    // Draw impact rings for all past hits
    for (let i = 0; i < this.hitTimes.length; i++) {
      if (this.hitTimes[i] < 0) continue;
      const age = this.elapsed - this.hitTimes[i];
      const ringDuration = LINGER_MS + (i === this.hitTimes.length - 1 ? FADE_MS : BOUNCE_MS);
      if (age > ringDuration) continue;
      this.drawImpact(ctx, this.positions[i + 1], age / ringDuration);
    }

    // Find current phase and draw blade
    let bladePos: Point | null = null;
    let bladeAlpha = 1;

    for (const { phase, start, duration } of this.phases) {
      if (this.elapsed < start) break;
      if (this.elapsed > start + duration) continue;
      const t = (this.elapsed - start) / duration;

      if (phase.kind === "travel") {
        const eased = Easing.easeOutQuad(t);
        bladePos = {
          x: phase.from.x + (phase.to.x - phase.from.x) * eased,
          y: phase.from.y + (phase.to.y - phase.from.y) * eased,
        };
        bladeAlpha = 1;
      } else if (phase.kind === "linger") {
        bladePos  = phase.at;
        bladeAlpha = 1;
      } else if (phase.kind === "fade") {
        bladePos  = phase.at;
        bladeAlpha = 1 - Easing.easeInQuad(t);
      }
    }

    if (!bladePos) return;

    // Glow aura
    ctx.save();
    ctx.globalAlpha = bladeAlpha * 0.3;
    ctx.fillStyle   = "#ffaa00";
    ctx.shadowColor = "#ffdd44";
    ctx.shadowBlur  = 30;
    ctx.beginPath();
    ctx.arc(bladePos.x, bladePos.y, BLADE_R * 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Trail: sample positions over last 80ms
    this.drawTrail(ctx, bladePos);

    drawBlade(ctx, bladePos.x, bladePos.y, rot, bladeAlpha);

    // Linger pulse ring — shows the blade is "locked on"
    this.drawLingerPulse(ctx, bladePos, rot);
  }

  private drawTrail(ctx: CanvasRenderingContext2D, tip: Point): void {
    const SAMPLES   = 7;
    const TRAIL_AGE = 80;

    const pts: Point[] = [];
    for (let s = SAMPLES; s >= 0; s--) {
      const t2 = Math.max(0, this.elapsed - (s / SAMPLES) * TRAIL_AGE);
      pts.push(this.samplePos(t2));
    }

    for (let s = 0; s < pts.length - 1; s++) {
      const a = pts[s];
      const b = pts[s + 1];
      const frac  = s / (pts.length - 1);
      ctx.save();
      ctx.globalAlpha = frac * 0.5;
      ctx.strokeStyle = "#f0c030";
      ctx.lineWidth   = 1.5 + frac * (BLADE_R * 0.85);
      ctx.lineCap     = "round";
      ctx.shadowColor = "#ffaa00";
      ctx.shadowBlur  = 8;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawLingerPulse(ctx: CanvasRenderingContext2D, at: Point, rot: number): void {
    // Find if we're currently in a linger phase
    for (const { phase, start, duration } of this.phases) {
      if (phase.kind !== "linger") continue;
      if (this.elapsed < start || this.elapsed > start + duration) continue;
      const t = (this.elapsed - start) / duration;
      // Pulsing ring that breathes in/out
      const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 3);
      ctx.save();
      ctx.globalAlpha = 0.6 * pulse;
      ctx.strokeStyle = "#ffe066";
      ctx.lineWidth   = 2;
      ctx.shadowColor = "#ffaa00";
      ctx.shadowBlur  = 14;
      ctx.beginPath();
      ctx.arc(at.x, at.y, BLADE_R * 1.5 + pulse * 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      break;
    }
  }

  private samplePos(t: number): Point {
    for (const { phase, start, duration } of this.phases) {
      if (t < start) break;
      if (t > start + duration) continue;
      const p = (t - start) / duration;
      if (phase.kind === "travel") {
        const eased = Easing.easeOutQuad(p);
        return {
          x: phase.from.x + (phase.to.x - phase.from.x) * eased,
          y: phase.from.y + (phase.to.y - phase.from.y) * eased,
        };
      }
      if (phase.kind === "linger" || phase.kind === "fade") {
        return phase.at;
      }
    }
    return this.positions[this.positions.length - 1];
  }

  private drawImpact(ctx: CanvasRenderingContext2D, pos: Point, t: number): void {
    const fade = 1 - Easing.easeInQuad(Math.min(t, 1));

    ctx.save();

    // White flash on arrival
    if (t < 0.2) {
      const f = 1 - t / 0.2;
      ctx.globalAlpha = f * 0.7;
      ctx.fillStyle   = "#ffffff";
      ctx.shadowColor = "#ffe066";
      ctx.shadowBlur  = 28;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, BLADE_R * 0.85 * f, 0, Math.PI * 2);
      ctx.fill();
    }

    // Expanding ring
    const r1 = BLADE_R * 0.4 + t * BLADE_R * 3.0;
    ctx.globalAlpha = fade * 0.85;
    ctx.strokeStyle = "#f0c030";
    ctx.lineWidth   = 3 - t * 1.5;
    ctx.shadowColor = "#ffaa00";
    ctx.shadowBlur  = 18;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, r1, 0, Math.PI * 2);
    ctx.stroke();

    // Softer inner ring
    const r2 = BLADE_R * 0.25 + t * BLADE_R * 1.8;
    ctx.globalAlpha = fade * 0.45;
    ctx.strokeStyle = "#ffe066";
    ctx.lineWidth   = 1.5;
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, r2, 0, Math.PI * 2);
    ctx.stroke();

    // 6 spark rays
    ctx.globalAlpha = fade * 0.75;
    ctx.strokeStyle = "#ffdd44";
    ctx.lineWidth   = 1.5;
    ctx.lineCap     = "round";
    ctx.shadowColor = "#f0a020";
    ctx.shadowBlur  = 7;
    for (let i = 0; i < 6; i++) {
      const a     = (i / 6) * Math.PI * 2 + t;
      const inner = BLADE_R * 0.2 + t * BLADE_R * 0.4;
      const outer = inner + BLADE_R * 1.3 * Easing.easeOutCubic(t);
      ctx.beginPath();
      ctx.moveTo(pos.x + Math.cos(a) * inner, pos.y + Math.sin(a) * inner);
      ctx.lineTo(pos.x + Math.cos(a) * outer, pos.y + Math.sin(a) * outer);
      ctx.stroke();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= this.total;
  }
}
